import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  createTenantQuoteApi,
  deleteTenantQuoteApi,
  getTenantQuoteApi,
  getTenantQuotesApi,
  updateTenantQuoteApi,
  updateTenantQuoteStatusApi,
} from "../../api/common/QuoteApi";

const unwrapResponse = (payload) => payload?.response ?? payload;

const createQuoteThunk = (type, api) =>
  createAsyncThunk(type, async (payload, { rejectWithValue }) => {
    try {
      const response = await api(payload);
      return response?.data;
    } catch (error) {
      return rejectWithValue({
        errorMessage:
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Something went wrong.",
      });
    }
  });

export const getTenantQuote = createQuoteThunk("quote/getTenantQuote", getTenantQuoteApi);
export const getTenantQuotes = createQuoteThunk("quote/getTenantQuotes", getTenantQuotesApi);
export const createTenantQuote = createQuoteThunk("quote/createTenantQuote", createTenantQuoteApi);
export const updateTenantQuote = createQuoteThunk("quote/updateTenantQuote", updateTenantQuoteApi);
export const deleteTenantQuote = createQuoteThunk("quote/deleteTenantQuote", deleteTenantQuoteApi);
export const updateTenantQuoteStatus = createQuoteThunk(
  "quote/updateTenantQuoteStatus",
  updateTenantQuoteStatusApi
);

const initialState = {
  quote: null,
  quotes: [],
  statusResponse: null,
  page: 0,
  size: 100,
  sortBy: "quoteId",
  sortDir: "desc",
  loading: false,
  loadingQuotes: false,
  saving: false,
  deleting: false,
  updatingStatus: false,
  error: null,
};

const quoteSlice = createSlice({
  name: "quote",
  initialState,
  reducers: {
    clearTenantQuote: (state) => {
      state.quote = null;
      state.statusResponse = null;
      state.error = null;
    },
    setTenantQuotePagination: (state, action) => {
      const { page, size, sortBy, sortDir } = action.payload || {};
      if (page !== undefined) state.page = page;
      if (size !== undefined) state.size = size;
      if (sortBy !== undefined) state.sortBy = sortBy;
      if (sortDir !== undefined) state.sortDir = sortDir;
    },
  },
  extraReducers: (builder) => {
    [
      [getTenantQuote, "loading", "quote"],
      [getTenantQuotes, "loadingQuotes", "quotes"],
      [createTenantQuote, "saving", "quote"],
      [updateTenantQuote, "saving", "quote"],
      [deleteTenantQuote, "deleting"],
      [updateTenantQuoteStatus, "updatingStatus", "statusResponse"],
    ].forEach(([thunk, loadingKey, resultKey]) => {
      builder
        .addCase(thunk.pending, (state, action) => {
          state[loadingKey] = true;
          state.error = null;
          if (thunk === getTenantQuotes) {
            const params = action.meta.arg || {};
            state.page = params.page ?? 0;
            state.size = params.size ?? 100;
            state.sortBy = params.sortBy ?? "quoteId";
            state.sortDir = params.sortDir ?? "desc";
          }
          if (thunk === updateTenantQuoteStatus) state.statusResponse = null;
        })
        .addCase(thunk.fulfilled, (state, action) => {
          state[loadingKey] = false;
          if (resultKey) state[resultKey] = unwrapResponse(action.payload);
          if (thunk === deleteTenantQuote && state.quote?.quoteId === action.meta.arg.quoteId) {
            state.quote = null;
          }
          if (thunk === updateTenantQuoteStatus && state.quote?.quoteId === action.meta.arg.quoteId) {
            state.quote.quoteStatus = action.meta.arg.quoteStatus;
            if (action.meta.arg.quoteAdminRemarks !== undefined) {
              state.quote.quoteAdminRemarks = action.meta.arg.quoteAdminRemarks;
            }
          }
        })
        .addCase(thunk.rejected, (state, action) => {
          state[loadingKey] = false;
          state.error = action.payload?.errorMessage || action.error?.message;
        });
    });
  },
});

export const { clearTenantQuote, setTenantQuotePagination } = quoteSlice.actions;
export default quoteSlice.reducer;
